import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  handleSignIn,
  handleSignOut,
  getAuthenticatedUser,
  isLoggedIn,
  type AuthenticatedUser,
  type UserCredentials,
} from '@/services/auth';

interface AuthState {
  user: AuthenticatedUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  // Actions
  signIn: (credentials: UserCredentials) => Promise<boolean>;
  signOut: () => Promise<void>;
  checkAuth: () => Promise<boolean>;
  clearError: () => void;
  setUser: (user: AuthenticatedUser | null) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      isAuthenticated: false,
      isLoading: true,
      error: null,

      signIn: async (credentials: UserCredentials) => {
        set({ isLoading: true, error: null });
        try {
          await handleSignIn(credentials);
          const user = await getAuthenticatedUser();
          set({
            user,
            isAuthenticated: true,
            isLoading: false,
          });
          return true;
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Sign in failed';
          set({
            error: message,
            isLoading: false,
            isAuthenticated: false,
          });
          return false;
        }
      },

      signOut: async () => {
        set({ isLoading: true });
        try {
          await handleSignOut();
        } finally {
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
          });
        }
      },

      checkAuth: async () => {
        set({ isLoading: true });
        try {
          const authenticated = await isLoggedIn();
          if (authenticated) {
            const user = await getAuthenticatedUser();
            set({
              user,
              isAuthenticated: true,
              isLoading: false,
            });
            return true;
          } else {
            set({
              user: null,
              isAuthenticated: false,
              isLoading: false,
            });
            return false;
          }
        } catch {
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
          });
          return false;
        }
      },

      clearError: () => set({ error: null }),

      setUser: (user) => set({ user, isAuthenticated: !!user }),
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        // Only persist minimal data
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
